import {
  CovToast,
  ToastStyle,
  ToastType,
} from "@cx-component-library/react-components";
import FavoriteIcon from "@mui/icons-material/Favorite";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import { Button } from "@mui/material";
import { RemoveFromCollectionDialog } from "components/dialog/RemoveFromCollectionDialog";
import { useCanBookmarkToCollection } from "hooks/use-can-bookmark-to-collection";
import {
  type CreateCollectionOutcome,
  useCollectionBookmark,
} from "hooks/use-collection-bookmark";
import { type FC, type MouseEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  type CollectionMembership,
  LOUNGE_COLLECTIONS_URL,
} from "service/collections";
import { CreateCollectionFlow } from "./CreateCollectionFlow";
import { SelectCollectionPopover } from "./SelectCollectionPopover";

export interface CollectionBookmarkButtonProps {
  eljExperimentId: string;
}

interface Toast {
  type: ToastType;
  message: string;
}

/** Heart that saves a material into Customer Lounge collections (CX-3519). */
export const CollectionBookmarkButton: FC<CollectionBookmarkButtonProps> = ({
  eljExperimentId,
}) => {
  const canBookmark = useCanBookmarkToCollection();
  return canBookmark ? (
    <CollectionBookmark eljExperimentId={eljExperimentId} />
  ) : null;
};

/** The ungated heart + popover; render through {@link CollectionBookmarkButton}. */
export const CollectionBookmark: FC<CollectionBookmarkButtonProps> = ({
  eljExperimentId,
}) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  // The heart's state loads on mount; the list only while the popover is open.
  const bookmark = useCollectionBookmark(eljExperimentId, anchorEl !== null);
  const [toRemove, setToRemove] = useState<CollectionMembership | null>(null);
  const [creating, setCreating] = useState(false);
  const [toast, setToast] = useState<Toast | null>(null);

  const showError = (message: string) =>
    setToast({ type: ToastType.DANGER, message });

  const save = async (collection: CollectionMembership) => {
    try {
      await bookmark.add(collection);
      setToast({
        type: ToastType.SUCCESS,
        message: t("src_collection_saved_toast", { name: collection.name }),
      });
    } catch {
      showError(t("src_collection_save_error", { name: collection.name }));
    }
  };

  const confirmRemove = async () => {
    const collection = toRemove;
    setToRemove(null);
    if (!collection) {
      return;
    }
    try {
      await bookmark.remove(collection);
    } catch {
      showError(t("src_collection_remove_error", { name: collection.name }));
    }
  };

  const openCreate = () => {
    // The dialog replaces the popover rather than stacking a second modal on it.
    setAnchorEl(null);
    setCreating(true);
  };

  const handleCreated = ({
    collection,
    saveFailed,
    moodboardFailed,
  }: CreateCollectionOutcome) => {
    setCreating(false);
    const { name } = collection;
    if (saveFailed) {
      showError(t("src_collection_save_error", { name }));
    } else if (moodboardFailed) {
      showError(t("src_collection_moodboard_error", { name }));
    } else {
      setToast({
        type: ToastType.SUCCESS,
        message: t("src_collection_saved_toast", { name }),
      });
    }
  };

  const handleOpen = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const Icon = bookmark.isSaved ? FavoriteIcon : FavoriteBorderIcon;

  return (
    <>
      <Button
        title={t("src_result_grid_bookmark")}
        aria-label={t("src_result_grid_bookmark")}
        aria-pressed={bookmark.isSaved}
        aria-haspopup="dialog"
        size="small"
        onClick={handleOpen}
        sx={{
          boxShadow: (theme) => theme.shadows[3],
          color: bookmark.isSaved
            ? "var(--alias-icon-on-white-brand, #009fe4)"
            : "var(--alias-icon-on-white-primary, #000)",
          backgroundColor: "var(--alias-surface-lowest, #fff)",
          height: "var(--spacing24, 24px)",
          minWidth: "var(--spacing24, 24px)",
          borderRadius: "2px",
          padding: 0,
          border: "1px solid var(--alias-stroke-on-white-default, #d9d9d9)",
          "&:hover": {
            backgroundColor: "var(--alias-stroke-on-white-default, #d9d9d9)",
          },
        }}
      >
        <Icon
          fontSize="small"
          sx={{
            margin: "auto",
            width: "var(--spacing14, 14px)",
            height: "var(--spacing14, 14px)",
          }}
        />
      </Button>
      <SelectCollectionPopover
        anchorEl={anchorEl}
        open={anchorEl !== null}
        onClose={() => setAnchorEl(null)}
        status={bookmark.status}
        collections={bookmark.collections}
        pendingIds={bookmark.pendingIds}
        onToggle={(collection) =>
          collection.contains ? setToRemove(collection) : void save(collection)
        }
        onCreate={openCreate}
        onRetry={() => void bookmark.reload()}
        allCollectionsUrl={LOUNGE_COLLECTIONS_URL}
      />
      {creating && (
        <CreateCollectionFlow
          onClose={() => setCreating(false)}
          create={({ body, moodboardFile }) =>
            bookmark.create(body, moodboardFile)
          }
          onCreated={handleCreated}
        />
      )}
      <RemoveFromCollectionDialog
        open={toRemove !== null}
        collectionName={toRemove?.name ?? ""}
        onCancel={() => setToRemove(null)}
        onConfirm={() => void confirmRemove()}
      />
      <CovToast
        visible={toast !== null}
        toastType={toast?.type}
        toastStyle={ToastStyle.FILLED}
        message={toast?.message ?? ""}
        hasCancelIcon
        autoHideDuration={6000}
        onClose={() => setToast(null)}
      />
    </>
  );
};
